import React, { useMemo, useState } from 'react';
import {
  X,
  Search,
  ChevronRight,
  Link2,
} from 'lucide-react';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import {
  filterCatalogGroups,
  getCatalogContext,
  getVisibleCatalogGroups,
} from '../../utils/workflowActionCatalog';
import WorkflowEditorPanelShell from './WorkflowEditorPanelShell';

const colorClasses = {
  emerald: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-700',
  purple: 'bg-purple-100 text-purple-700',
  orange: 'bg-orange-100 text-orange-700',
  slate: 'bg-slate-100 text-slate-600',
  blue: 'bg-blue-100 text-blue-600',
  green: 'bg-green-100 text-green-600',
  teal: 'bg-teal-100 text-teal-700',
  pink: 'bg-pink-100 text-pink-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  cyan: 'bg-cyan-100 text-cyan-700',
  gray: 'bg-gray-100 text-gray-600',
  red: 'bg-red-100 text-red-600',
  yellow: 'bg-yellow-100 text-yellow-700',
};

const ActionItem = ({ action, onClick, theme = 'light' }) => {
  const Icon = action.icon;
  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      onClick={() => onClick(action)}
      className={cn(
        'group w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left',
        isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-50',
      )}
      data-testid={`action-${action.id}`}
    >
      <div className={cn(
        'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105',
        colorClasses[action.color] || colorClasses.emerald,
      )}>
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn('font-medium text-sm', isDark ? 'text-white' : 'text-slate-900')}>{action.name}</p>
        <p className={cn('text-xs truncate', isDark ? 'text-zinc-500' : 'text-slate-500')}>{action.description}</p>
      </div>
      <ChevronRight size={16} className={isDark ? 'text-zinc-600 group-hover:text-zinc-400' : 'text-slate-300 group-hover:text-slate-400'} />
    </button>
  );
};

const WorkflowActionsPanel = ({
  onClose,
  onSelectAction,
  theme = 'light',
  advancedMode = false,
  allowAdvancedActions = false,
  allowDataTools = false,
  extraGroups = [],
  isOpen = true,
  width,
  overlayMode = false,
  onOverlayDismiss,
  activePanelType = 'actions',
  chromeHeight,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('actions');

  const catalogContext = useMemo(
    () => getCatalogContext({ advancedMode, allowAdvancedActions, allowDataTools }),
    [advancedMode, allowAdvancedActions, allowDataTools],
  );

  const visibleGroups = useMemo(() => {
    const catalog = getVisibleCatalogGroups(catalogContext);
    const merged = [...catalog, ...extraGroups];
    return filterCatalogGroups(merged, searchQuery, catalogContext);
  }, [catalogContext, extraGroups, searchQuery]);

  const isDark = theme === 'dark';
  const panelWidth = width ?? (isDark ? 400 : 320);

  return (
    <WorkflowEditorPanelShell
      isOpen={isOpen}
      width={panelWidth}
      overlayMode={overlayMode}
      chromeHeight={chromeHeight}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      className={cn(
        isDark ? 'bg-zinc-900 border-l border-zinc-700' : 'bg-white border-l border-slate-200',
      )}
      testId="actions-panel"
    >
      <div
        className="flex h-full min-h-0 flex-col"
        data-workflow-side-panel
      >
      <div className={cn('flex items-center justify-between px-4 py-3 border-b', isDark ? 'border-zinc-700' : 'border-slate-200')}>
        <h3 className={cn('font-semibold', isDark ? 'text-white' : 'text-slate-900')}>Add Action</h3>
        <button type="button" onClick={onClose} className={cn('p-1.5 rounded-lg transition-colors', isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-100')}>
          <X size={18} className={isDark ? 'text-zinc-400' : 'text-slate-500'} />
        </button>
      </div>

      <div className={cn('flex border-b', isDark ? 'border-zinc-700' : 'border-slate-200')}>
        <button
          type="button"
          onClick={() => setActiveTab('actions')}
          className={cn(
            'flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'actions'
              ? 'border-emerald-600 text-emerald-600'
              : cn('border-transparent', isDark ? 'text-zinc-500 hover:text-white' : 'text-slate-500 hover:text-slate-700'),
          )}
        >
          Actions
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('apps')}
          className={cn(
            'flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'apps'
              ? 'border-emerald-600 text-emerald-600'
              : cn('border-transparent', isDark ? 'text-zinc-500 hover:text-white' : 'text-slate-500 hover:text-slate-700'),
          )}
        >
          Apps
        </button>
      </div>

      <div className={cn('p-3 border-b', isDark ? 'border-zinc-700' : 'border-slate-100')}>
        <div className="relative">
          <Search className={cn('absolute left-3 top-1/2 -translate-y-1/2', isDark ? 'text-zinc-500' : 'text-slate-400')} size={16} />
          <Input
            placeholder="Search actions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn('pl-9 h-9 text-sm', isDark && 'bg-zinc-800 border-zinc-700 text-white')}
            data-testid="workflow-action-search"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'actions' ? (
          <>
            {visibleGroups.map((group) => (
              <div key={group.id} className={cn('p-3 border-b last:border-0', isDark ? 'border-zinc-800' : 'border-slate-100')}>
                <h4 className={cn(
                  'text-xs font-semibold uppercase tracking-wide mb-2 px-1',
                  isDark ? 'text-zinc-500' : 'text-slate-500',
                )}
                >
                  {group.name}
                </h4>
                <div className="space-y-1">
                  {group.actions.map((action) => (
                    <ActionItem key={action.id} action={action} onClick={onSelectAction} theme={theme} />
                  ))}
                </div>
              </div>
            ))}

            {visibleGroups.length === 0 && searchQuery && (
              <div className="p-8 text-center">
                <Search className={cn('mx-auto mb-3', isDark ? 'text-zinc-600' : 'text-slate-300')} size={32} />
                <p className={cn('text-sm', isDark ? 'text-zinc-500' : 'text-slate-500')}>No actions found</p>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center">
            <Link2 className={cn('mx-auto mb-3', isDark ? 'text-zinc-600' : 'text-slate-300')} size={32} />
            <p className={cn('text-sm font-medium mb-1', isDark ? 'text-zinc-300' : 'text-slate-700')}>App Integrations</p>
            <p className={cn('text-xs', isDark ? 'text-zinc-500' : 'text-slate-500')}>Connect external apps and services</p>
          </div>
        )}
      </div>
      </div>
    </WorkflowEditorPanelShell>
  );
};

export default WorkflowActionsPanel;
